import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { Spinner, ErrorBox, Success, DataTable } from '../components/ui';

/**
 * Roles & permissions admin. Create new roles, assign permissions to them,
 * and create new permissions. Permissions are grouped by module so the
 * checkbox grid stays readable as they grow.
 */
export default function Roles() {
  const [roles, setRoles] = useState(null);
  const [perms, setPerms] = useState([]);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null);      // role being edited (or 'new')
  const [form, setForm] = useState({ name: '', description: '', permissionIds: [] });
  const [permForm, setPermForm] = useState({ code: '', name: '', module: '' });
  const [showPermForm, setShowPermForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get('/admin/roles').then(r => setRoles(r.data)).catch(setErr);
    api.get('/admin/permissions').then(r => setPerms(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const flash = m => { setMsg(m); setTimeout(() => setMsg(null), 2500); };

  const openNew = () => { setForm({ name: '', description: '', permissionIds: [] }); setEditing('new'); };
  const openEdit = async id => {
    const r = await api.get(`/admin/roles/${id}`);
    setForm({ name: r.data.name, description: r.data.description || '', permissionIds: r.data.permissionIds, is_system: r.data.is_system });
    setEditing(id);
  };
  const togglePerm = id => setForm(f => ({ ...f, permissionIds: f.permissionIds.includes(id) ? f.permissionIds.filter(x => x !== id) : [...f.permissionIds, id] }));

  const saveRole = async e => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      if (editing === 'new') await api.post('/admin/roles', form);
      else await api.put(`/admin/roles/${editing}`, form);
      flash('Role saved.'); setEditing(null); load();
    } catch (e2) { setErr(e2); } finally { setBusy(false); }
  };

  const savePerm = async e => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      await api.post('/admin/permissions', permForm);
      flash('Permission created.'); setPermForm({ code: '', name: '', module: '' }); setShowPermForm(false); load();
    } catch (e2) { setErr(e2); } finally { setBusy(false); }
  };

  if (!roles) return <Spinner label="Loading roles…" />;

  const byModule = {};
  perms.forEach(p => { (byModule[p.module] = byModule[p.module] || []).push(p); });

  return <>
    <div className="phead"><h1>Roles &amp; permissions</h1>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => setShowPermForm(s => !s)}>{showPermForm ? 'Close' : '+ New permission'}</button>
        <button className="primary" onClick={openNew}>+ New role</button>
      </div>
    </div>
    <Success message={msg} /><ErrorBox error={err} />

    {showPermForm && <div className="panel">
      <h3>Create permission</h3>
      <form onSubmit={savePerm}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div className="field"><label>Code</label><input style={{ width: '100%' }} placeholder="report.custom" value={permForm.code} onChange={e => setPermForm({ ...permForm, code: e.target.value })} required /></div>
          <div className="field"><label>Name</label><input style={{ width: '100%' }} placeholder="View custom reports" value={permForm.name} onChange={e => setPermForm({ ...permForm, name: e.target.value })} required /></div>
          <div className="field"><label>Module</label><input style={{ width: '100%' }} placeholder="Reports" value={permForm.module} onChange={e => setPermForm({ ...permForm, module: e.target.value })} required /></div>
        </div>
        <button className="primary" disabled={busy}>Create permission</button>
      </form>
    </div>}

    {editing !== null && <div className="panel">
      <h3>{editing === 'new' ? 'Create role' : 'Edit role'}</h3>
      <form onSubmit={saveRole}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
          <div className="field"><label>Role name</label>
            <input style={{ width: '100%' }} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required disabled={form.is_system} />
            {form.is_system ? <div className="muted" style={{ marginTop: 3 }}>System role — name is fixed, permissions editable.</div> : null}</div>
          <div className="field"><label>Description</label><input style={{ width: '100%' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
        </div>
        <div className="field"><label>Permissions ({form.permissionIds.length} selected)</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
            {Object.keys(byModule).sort().map(mod => <div key={mod} style={{ border: '1px solid var(--line)', borderRadius: 6, padding: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6, color: 'var(--navy)' }}>{mod}</div>
              {byModule[mod].map(p => <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 4px', fontSize: 12.5 }} title={p.code}>
                <input type="checkbox" checked={form.permissionIds.includes(p.id)} onChange={() => togglePerm(p.id)} /> {p.name}</label>)}
            </div>)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="primary" disabled={busy}>{busy ? 'Saving…' : 'Save role'}</button>
          <button type="button" onClick={() => setEditing(null)}>Cancel</button>
        </div>
      </form>
    </div>}

    <div className="panel">
      <DataTable rows={roles} columns={[
        { key: 'name', label: 'Role' },
        { key: 'description', label: 'Description' },
        { key: 'permission_count', label: 'Permissions' },
        { key: 'user_count', label: 'Users' },
        { key: 'is_system', label: 'Type', render: v => v ? <span className="badge LRB">System</span> : <span className="badge Mixed">Custom</span> },
        { key: 'id', label: '', render: (v, r) => <button onClick={() => openEdit(v)}>Edit</button> }
      ]} />
    </div>
  </>;
}
